import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2 } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { IoMdSearch } from "react-icons/io";
import { TbTableColumn } from "react-icons/tb";
import { toast } from "react-toastify";
// import { ThreeDots } from "react-loader-spinner";

import { getallContractors } from "../../../../Redux/actions/Operationsactions/getcontractors";
import {
  // AddContractorApi,
  DeleteContractorApi,
  // UpdateContractorApi,
} from "../../../../Api/Operations/Contractors";
import ActionButton from "../../../Common/ActionButton";
import DeleteConfirmModal from "../../../../Common/DeleteConfirmModal";
import { useSession } from "../../../../context/SessionContext";

/**
 * Contractors List Component
 * Displays contractors in AG Grid with search, filtering, column visibility
 * Pattern: Follows existing module list structure
 */
const ContractorsListComp = () => {
  const resultAllContractors = useSelector((state) => state.getAllContractors);
  const rolesData = useSelector((state) => state.GetAllRole);
  const { session } = useSession();
  const projectId = session?.ProjectId;
  const history = useHistory();
  const gridRef = useRef(null);
  const colDropdownRef = useRef(null);
  const [colDropdownOpen, setColDropdownOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();

  const [selectedContractor, setSelectedContractor] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Default column definition for AG Grid
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (params) => params.value,
      valueFormatter: (params) =>
        params.value === null || params.value === undefined || params.value === ""
          ? "N/A"
          : params.value,
    }),
    [],
  );

  // Column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Name",
        field: "Name",
        flex: 1,
      },
      {
        headerName: "Email",
        field: "Email",
        flex: 1,
      },
      {
        headerName: "Phone Number",
        field: "PhoneNumber",
        flex: 1,
      },
      {
        headerName: "Contact No 1",
        field: "ContactNo1",
        flex: 1,
      },
      {
        headerName: "Contact No 2",
        field: "ContactNo2",
        flex: 1,
      },
      {
        headerName: "City",
        field: "City",
        width: 100,
      },
      {
        headerName: "State",
        field: "State",
        width: 100,
      },
      // Action column (conditional based on roles)
      ...(rolesData?.data?.UserRoles?.includes("Store Contractors_Edit") ||
      rolesData?.data?.UserRoles?.includes("Store Contractors_Create")
        ? [
            {
              headerName: "Action",
              colId: "action",
              width: 120,
              filter: false,
              sortable: false,
              headerComponent: () => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  {rolesData?.data?.UserRoles?.includes("Store Contractors_Create") ? (
                    <ActionButton
                      text="New+"
                      newBtn="true"
                      onClick={() => history.push("/store/contractors/contractors/add")}
                    />
                  ) : (
                    <span>Action</span>
                  )}
                </div>
              ),
              cellRenderer: (params) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    gap: "12px",
                  }}
                >
                  {rolesData?.data?.UserRoles?.includes("Store Contractors_Edit") && (
                    <FiEdit2
                      title="Edit"
                      size="1.1em"
                      style={{ cursor: "pointer", color: "#4e545a" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push("/store/contractors/contractors/add", params.data);
                      }}
                    />
                  )}
                  {rolesData?.data?.UserRoles?.includes("Store Contractors_Delete") && (
                    <MdDeleteForever
                      title="Delete"
                      size="1.1em"
                      className="cur_sor"
                      style={{ cursor: "pointer", color: "#c0392b" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedContractor(params.data);
                      }}
                    />
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [rolesData, history],
  );

  // Fetch data on mount
  useEffect(() => {
    dispatch(getallContractors());
  }, [dispatch]);

  // On grid ready
  const onGridReady = useCallback((params) => {
    const visibilityMap = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        visibilityMap[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(visibilityMap);
  }, []);

  // Toggle column visibility
  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({
        ...prev,
        [colId]: !isCurrentlyVisible,
      }));
    },
    [columnVisibility],
  );

  // Delete contractor
  const handleConfirmDelete = async () => {
    if (!selectedContractor) {
      return;
    }
    try {
      setIsDeleting(true);
      const result = await DeleteContractorApi(selectedContractor?.Id, projectId);
      if (result.data?.IsSuccess) {
        toast.success("Contractor deleted successfully");
        dispatch(getallContractors());
        setSelectedContractor(null);
      } else {
        toast.error(result.data?.Message || "Failed to delete contractor");
      }
    } catch (error) {
      toast.error("Error deleting contractor");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Contractors"
        >
          Contractors
        </div>
        {/* Search Input */}
        <div className="d-flex align-items-center max-w-250" style={{ position: "relative" }}>
          <IoMdSearch
            size="1.1em"
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#4e545a",
            }}
          />
          <input
            type="text"
            className="input_styling"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "250px", paddingLeft: 30 }}
            autoComplete="off"
          />
        </div>

        {/* Column Visibility Dropdown */}
        <div ref={colDropdownRef} style={{ position: "relative", flexGrow: 0 }}>
          <button
            className="input_styling"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              padding: "4px 10px",
              whiteSpace: "nowrap",
              marginLeft: "10px",
            }}
            onClick={() => setColDropdownOpen((prev) => !prev)}
            title="Show / Hide Columns"
          >
            <TbTableColumn size="1.3em" />
            <span>Columns</span>
          </button>

          {colDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "110%",
                right: 0,
                zIndex: 9999,
                background: "#fff",
                border: "1px solid #E8FBFB",
                borderRadius: 6,
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                padding: "8px 0",
                minWidth: 180,
              }}
            >
              {columnDefs.map((col) => (
                <label
                  key={col.colId || col.field}
                  style={{
                    display: "block",
                    padding: "8px 12px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={columnVisibility[col.colId || col.field] !== false}
                    onChange={() => toggleColumn(col.colId || col.field)}
                    style={{ marginRight: "8px" }}
                  />
                  {col.headerName}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AG Grid Table */}
      <div
        style={{ width: "100%", height: "800px", marginTop: "20px" }}
        className="ag-theme-alpine"
      >
        <AgGridReact
          ref={gridRef}
          rowData={resultAllContractors?.data || []}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          pagination={true}
          paginationPageSize={10}
          quickFilterText={searchTerm}
        />
      </div>

      <DeleteConfirmModal
        open={!!selectedContractor}
        onClose={() => setSelectedContractor(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        message={`Are you sure you want to delete contractor "${selectedContractor?.Name}"?`}
      />
    </>
  );
};

export { ContractorsListComp };
